import { useEffect, useMemo, useState } from "react";
import Aside from "../../components/Aside";
import CustomDataTable from "../../components/CustomDataTable";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { AlertService } from "../../services/Alert";
import { UsersService } from "../../services/UsersService";
import ModalUser from "./ModalUser";
import ModalAddSubAccount from "./ModalAddSubAccount";
import { useSession } from "../../hooks/useSession";
import { PROFILES } from "../../constants";
import { useNavigate } from "react-router-dom";
import InputCopyClipboard from "../../components/InputCopyClipboard";
import { useTranslation } from "react-i18next";
import useRequest from "../../hooks/useRequest";
import usePermission from "../../hooks/usePermission";
import HasAccess from "../../guards/HasAccess";

export default function UsersPage() {

    const { t } = useTranslation();
    const { call, isRequesting } = useRequest();
    const [showModal, setShowModal] = useState(false);
    const [showModalSub, setShowModalSub] = useState(false);
    const [listUsers, setListUsers] = useState([]);
    const [accountsSelected, setAccountsSelected] = useState([]);
    const [subAccountId, setSubAccountId] = useState({ userId: 0, subaccount: '' });
    const [userToEdit, setUserToEdit] = useState();
    const { session } = useSession();
    const navigate = useNavigate();
    const hasCreationAccess = usePermission('user.create');

    const getAll = async () => {

        const allData = {
            user_id: session.id,
            profile: session.profile
        }

        try {
            const { data } = await call(() => UsersService.getAllUsers(allData));
            const list = data.map(user => (
                {
                    id: user.id,
                    username: user.username,
                    account_id: user.account_id,
                    email: user.email,
                    fullname: user.complete_name,
                    accounts: user.accounts.map(a => ({
                        account_name: a.account_name,
                        account_id: a.account_id
                    })),
                    acc_name: user.account_name,
                    area: user.area,
                    profile: user.profile,
                    type: user.id_profile,
                    status: user.status,
                }
            ));

            setListUsers(list);
        } catch (error) {
            console.log(error);
        }
    }

    const columns = useMemo(() => [
        {
            name: t("platform.users.id"),
            sortable: true,
            selector: row => row.id,
        },
        {
            name: t("platform.users.username"),
            sortable: true,
            selector: row => row.username,
        },
        {
            name: t("platform.users.name"),
            sortable: true,
            selector: row => row.fullname,
        },
        {
            name: t("platform.users.account"),
            sortable: true,
            formatToExport: row => row.accounts.map(account => account.account_name).join(', '),
            selector: row => row.accounts.map(account => account.account_name).slice(0, 2).join(', ') + (row.accounts.length > 2 ? ' +' + (row.accounts.length - 2) + ' más' : '')
        },
        {
            name: t("platform.users.subaccount"),
            sortable: true,
            selector: row => row.area,
        },
        {
            name: t("platform.users.profile"),
            sortable: true,
            selector: row => row.profile,
        },
        {
            name: t("platform.users.status"),
            sortable: true,
            selector: row => row.status,
            formatToExport: row => row.status === 1 ? 'Activo' : 'Inactivo',
            cell: (row) => {
                return (
                    row.status === 1 ?
                        (
                            <span className="badge bg-soft-info text-info">
                                <span className="legend-indicator bg-info"></span> Activo
                            </span>
                        ) :
                        (
                            <span className="badge bg-soft-danger text-danger">
                                <span className="legend-indicator bg-danger"></span> Inactivo
                            </span>
                        )
                )
            }
        },
        {
            name: t("platform.users.actions"),
            sortable: false,
            center: true,
            exportable: false,
            cell: (row) => {
                if (row.profile.toLowerCase() === PROFILES.BOT || row.profile.toLowerCase() === PROFILES.IVR) return '';

                return (
                    <HasAccess permission="user.actions">
                        <div className="btn-group" role="group">
                            <button className="btn bg-soft-info-alternative btn-sm text-white-dark" onClick={() => showFormUserEdit(row)}>
                                <i className="bi-pencil-fill me-1 text-primary"></i> {t("platform.users.edit")}
                            </button>

                            <div className="btn-group">
                                <button type="button" className="btn bg-soft-info-alternative btn-sm text-white-dark btn-icon btn-sm dropdown-toggle dropdown-toggle-empty" data-bs-toggle="dropdown" aria-expanded="false"></button>

                                <div className="dropdown-menu dropdown-menu-end mt-1" >
                                    <button className="dropdown-item" onClick={() => changePassword(row.id)}>
                                        {t("platform.users.reset-password")}
                                    </button>
                                    <button className="dropdown-item" onClick={() => showModalSubAccount(row.id, row.area)}>
                                        {t("platform.users.change-subaccount")}
                                    </button>
                                    {
                                        row.profile.toLowerCase() === PROFILES.SUPERVISOR &&
                                        <button className="dropdown-item" onClick={() => navigate(`/users/supervisor/permissions`, { state: { user: row } })}>
                                            {t("platform.users.permissions")}
                                        </button>
                                    }
                                    <button className="dropdown-item" onClick={() => changeStatus(row.id, row.status)}>
                                        {
                                            row.status === 1 ? t("platform.users.disable") : t("platform.users.enable")
                                        }
                                    </button>
                                    <button className="dropdown-item" onClick={() => deleteUser(row.id)}>
                                        {t("platform.users.delete")}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </HasAccess>
                )
            }
        },
    ], [])

    const deleteUser = async (id) => {
        const isConfirmed = await AlertService.warning('Borrar usuario', '¿Estás seguro de querer borrar este usuario? Esta acción no se puede deshacer', async () => {
            try {
                const res = await UsersService.deleteUser({ user_id: id });
                if (res.code === 200) {
                    await getAll();
                }
            } catch (error) {
                console.log(error)
            }
        });

        if (isConfirmed) {
            AlertService.success('¡Éxito!', "Se borró correctamente al usuario", 'Ok');
        }

    }

    const changeStatus = async (id, status) => {

        const isConfirmed = await AlertService.warning('Aviso', '¿Estás seguro de querer cambiar el estatus del usuario?', async () => {
            try {

                const data = {
                    user_id: id,
                    status: !status
                }

                const res = await UsersService.changeStatusUser(data);
                if (res.code === 200) {
                    await getAll();
                }
            } catch (error) {
                console.log(error);
            }
        });

        if (isConfirmed) {
            AlertService.success('¡Éxito!', "Se cambio el estatus correctamente al usuario", 'Ok');
        }
    }



    const changePassword = async (id) => {
        await AlertService.warning('Aviso', '¿Estás seguro de querer restablecer la contraseña al usuario?', async () => {
            try {
                const { data: { new_pass } } = await UsersService.changePasswordUser({ user_id: id });
                // AlertService.success('Nueva contraseña', new_pass, 'OK')

                const htlm = (
                    <div>
                        <p>La nueva contraseña es:</p>
                        <div className="width-fit-content m-auto">
                            <InputCopyClipboard text={new_pass} />
                        </div>

                    </div>
                )

                await AlertService.success(<p className="h2">Contraseña restablecida existosamente</p>, htlm)

            } catch (error) {
                console.log(error)
            }
        });
    }

    const showModalSubAccount = (id, subaccount) => {
        setShowModalSub(true);
        setSubAccountId({
            userId: id,
            subaccount: subaccount || ''
        });
    }

    const showFormUser = () => {
        setShowModal(true);
    }

    const showFormUserEdit = (row) => {
        setShowModal(true);
        setUserToEdit(row);
    }

    const handleHideModal = () => {
        setUserToEdit(null)
        setShowModal(false)
    }

    const listUsersFiltered = useMemo(() => {
        if (!accountsSelected.length) return listUsers;

        const exists = (accounts) => {
            return accounts.some(r => accountsSelected.map(a => +a.value).includes(+r.account_id))
        }

        return listUsers.filter(row => exists(row.accounts))
    }, [listUsers, accountsSelected])

    useEffect(() => {
        getAll();
    }, [])


    return (
        <>
            <Header />
            <Aside />

            <main id="content" role="main" className="main">

                <div className="content container-fluid">

                    <div className="page-header">
                        <div className="row align-items-center">
                            <div className="col-sm mb-2 mb-sm-0">
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb breadcrumb-no-gutter">
                                        <li className="breadcrumb-item "><span className="breadcrumb-link" >{t("platform.platform-title")}</span></li>
                                        <li className="breadcrumb-item active" aria-current="page">{t("platform.users.users-title")}</li>
                                    </ol>
                                </nav>
                                <h1 className="page-header-title text-white-dark"><i className="bi-people-fill text-primary"></i> {t("platform.users.users-title")}</h1>
                            </div>
                            {
                                hasCreationAccess ?
                                    <div className="col-sm-auto">
                                        <button className="btn btn-primary" onClick={showFormUser}><i className="bi-plus-lg me-1"></i> {t("platform.users.add-user")}</button>
                                    </div>
                                    : null
                            }
                        </div>
                    </div>

                    <CustomDataTable
                        columns={columns}
                        data={listUsersFiltered}
                        showExport
                        isLoading={isRequesting}
                        filter={(row, term) => {
                            if (!term) return true;

                            return (
                                `${row.id}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.username}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.fullname}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.account}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.area}`.match(new RegExp(`${term}`, 'i')) ||
                                `${row.type}`.match(new RegExp(`${term}`, 'i'))
                            )
                        }}
                        onAccountsSelected={(accounts) => setAccountsSelected(accounts)}
                    />

                </div>
                <Footer />
            </main>

            <ModalUser
                user={userToEdit}
                show={showModal}
                onRequestClose={handleHideModal}
                reload={getAll}
            />

            <ModalAddSubAccount
                getAll={getAll}
                subAccountId={subAccountId.userId}
                subaccountName={subAccountId.subaccount}
                showSub={showModalSub}
                onRequestClose={() => {
                    setSubAccountId({
                        userId: 0,
                        subaccount: ''
                    })
                    setShowModalSub(false)
                }}
            />

        </>
    )
}